import React, { useCallback, useEffect, useState } from 'react'
import {
  Box,
  Stack,
  Typography,
  Menu,
  MenuItem,
  Checkbox,
  Chip,
  OutlinedInput,
  useTheme,
  Divider,
  InputAdornment,
  IconButton,
  Button,
} from '@mui/material'
import { ExpandMore } from '@mui/icons-material'
import { J2Multiply } from '@e3dc-react/icons'
import { makeStyles } from 'tss-react/mui'
import { useDebounce } from '../../Hooks/useDebounce'
import { useCustomTranslation } from '../../Hooks/useCustomTranslation'

const useStyles = makeStyles()(theme => ({
  filterButton: {
    minHeight: 'unset', // prevent MUI theme override coming from muiTheme.ts to adhere to design
    maxHeight: 'unset', // prevent MUI theme override coming from muiTheme.ts to adhere to design
    height: theme.shape.filter.height,
    borderRadius: theme.spacing(theme.shape.borderRadius),
    display: 'inline-flex',
    alignItems: 'center',
    paddingLeft: theme.spacing(2.5),
    paddingRight: theme.spacing(1.5),
    userSelect: 'none',
    borderStyle: 'solid',
    borderWidth: 1,
    '&:hover': {
      cursor: 'pointer',
    },
  },
  filterButtonInactive: {
    '&:hover': {
      borderColor: theme.palette.filter.hoverBorder,
      backgroundColor: theme.palette.filter.inactiveBackground,
    },
    backgroundColor: theme.palette.filter.inactiveBackground,
    borderColor: theme.palette.filter.inactiveBorder,
    color: theme.palette.filter.inactiveText,
  },
  filterButtonActive: {
    backgroundColor: theme.palette.filter.activeBackground,
    borderColor: theme.palette.filter.activeBorder,
    color: theme.palette.filter.activeText,
    '&:hover': {
      borderColor: theme.palette.filter.activeBorder,
      backgroundColor: theme.palette.filter.activeBackground,
    },
  },
  filterButtonMenuOpen: {
    backgroundColor: theme.palette.filter.inactiveBackground,
  },
  filterCountChip: {
    marginLeft: theme.spacing(1),
    pointerEvents: 'none',
  },
  searchFieldContainer: {
    width: '100%',
    padding: theme.spacing(2),
    paddingTop: theme.spacing(0.5),
    position: 'relative',
    display: 'inline-block',
    verticalAlign: 'top',
  },
  searchField: {
    height: theme.shape.filter.searchFieldHeight,
    minHeight: 'unset', // prevent MUI theme override coming from muiTheme.ts to adhere to design
    maxHeight: 'unset', // prevent MUI theme override coming from muiTheme.ts to adhere to design
    backgroundColor: theme.palette.filter.searchFieldBackground,
    '& .MuiOutlinedInput-notchedOutline': {
      borderColor: 'transparent',
    },
  },
  noSearchResultsLabel: {
    paddingLeft: theme.spacing(2.5),
    paddingRight: theme.spacing(2.5),
    marginLeft: '1px',
    height: theme.shape.filter.menuItemHeight,
    color: theme.palette.text.disabled,
    userSelect: 'none',
    display: 'flex',
    alignItems: 'center',
  },
  menuItems: {
    /**
     * Filter consists of 3 sections: Search field, list of options and "Clear all" button.
     *
     * We let options take as much space as needed - which is default MUI Menu behavior. However, per design,
     * to keep Search field fixed on top at all times and "Clear all" button at the bottom, we need
     * to restrict the max height of the middle container.
     *
     * The deducted height is the minimal value which will enable scrolling on just the middle container.
     */
    maxHeight: 'calc(100vh - 150px)',
    overflow: 'auto',
  },
  menu: {
    backgroundColor: theme.palette.background.paper,
    '&.MuiMenu-paper': {
      padding: 0,
    },
  },
  menuItem: {
    minWidth: 150,
    height: theme.shape.filter.menuItemHeight,
    '&.MuiMenuItem-root:hover': {
      backgroundColor: theme.palette.filter.menuItemHoverBackground,
      '&.Mui-selected': {
        backgroundColor: theme.palette.filter.selectedMenuItemHoverBackground,
      },
    },
  },
  checkbox: {
    '.MuiSvgIcon-root': { height: 16, width: 16 },
  },
  clearAllMenuItem: {
    width: '100%',
    '&.MuiMenuItem-root:hover': {
      backgroundColor: theme.palette.filter.menuItemHoverBackground,
    },
  },
  dropdownIcon: {
    height: 20,
    width: 20,
    transition: 'transform 0.2s',
  },
  dropdownIconActive: {
    transform: 'rotate(180deg)',
  },
  hidden: {
    visibility: 'hidden',
  },
  divider: {
    borderColor: theme.palette.divider,
  },
  optionIcon: {
    paddingTop: theme.spacing(0.5),
    width: theme.spacing(6.5),
  },
}))

export type FilterOption<T> = {
  label: string
  value: T
  optionIcon?: JSX.Element
  menuItemContent?: JSX.Element
}

interface FilterProps<T> {
  options: FilterOption<T>[]
  value?: T[]
  label?: string
  searchable?: boolean
  disabled?: boolean
  dataQa?: string
  onChange: (value: T[]) => void
}

export const Filter = <T,>({ options, value, label, searchable, disabled, dataQa, onChange }: FilterProps<T>): React.ReactElement => {
  const { t } = useCustomTranslation()
  const theme = useTheme()
  const { classes, cx } = useStyles()
  const [menuAnchorElement, setMenuAnchorElement] = useState<null | HTMLElement>(null)
  const menuOpen = Boolean(menuAnchorElement)
  const [selectedValues, setSelectedValues] = useState<T[]>(value || [])
  const hasSelection = selectedValues.length > 0
  const [searchQuery, setSearchQuery] = useState<string>('')
  const debouncedSearchQuery = useDebounce(searchQuery, 300)
  const filteredOptions = options.filter(option => option.label.toLowerCase().includes(debouncedSearchQuery.toLowerCase()))

  const handleFilterClick = useCallback((event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    setMenuAnchorElement(event.currentTarget)
  }, [])

  const handleFilterOptionClick = useCallback(
    (option: FilterOption<T>) => {
      // adds or removes the clicked option from checked filters
      const newValues = selectedValues.includes(option.value) ? selectedValues.filter(i => i !== option.value) : [...selectedValues, option.value]

      setSelectedValues(newValues)
      onChange(newValues)
    },
    [onChange, selectedValues]
  )

  const clearSearchQuery = useCallback(() => {
    setSearchQuery('')
  }, [])

  const closeMenu = useCallback(() => {
    setMenuAnchorElement(null)

    // Clear search query after "close menu" animation is done to prevent the list of menu items jumping
    setTimeout(() => {
      clearSearchQuery()
    }, theme.transitions.duration.standard)
  }, [theme, clearSearchQuery])

  const handleClearAll = useCallback(() => {
    clearSearchQuery()
    setSelectedValues([])
    onChange([])
    closeMenu()
  }, [onChange, clearSearchQuery, closeMenu])

  // reset if 'value' changes externally, e.g. when user resets form in the parent or prefills it with new values
  useEffect(() => {
    setSelectedValues(value || [])
  }, [value])

  return (
    <>
      <Button
        className={cx(classes.filterButton, classes.filterButtonInactive, {
          [classes.filterButtonActive]: hasSelection || menuOpen,
          [classes.filterButtonMenuOpen]: menuOpen,
        })}
        onClick={handleFilterClick}
        data-qa={dataQa ? dataQa : label ? `${label.toLowerCase()}-filter-button` : 'filter-button'}
        aria-label={label ? `${label} filter button` : 'Filter button'}
        disabled={disabled}
      >
        <Stack direction="row">
          <Typography variant="caption" alignSelf="center">
            {label}
          </Typography>

          {hasSelection ? <Chip size="small" color="primary" label={selectedValues.length} className={classes.filterCountChip} /> : null}

          <ExpandMore className={cx(classes.dropdownIcon, { [classes.dropdownIconActive]: menuOpen })} />
        </Stack>
      </Button>

      <Menu
        anchorEl={menuAnchorElement}
        open={menuOpen}
        disableAutoFocusItem // prevents text field from losing focus when the list of menu items is re-rendered after new search query
        onClose={(_, reason) => {
          if (reason === 'backdropClick' || reason === 'escapeKeyDown') {
            // prevents closing menu by clicking an element inside Menu(Search field, Checkbox, "Clear all" button...)
            closeMenu()
          }
        }}
        PaperProps={{
          className: classes.menu,
        }}
        defaultValue={searchQuery}
        anchorOrigin={{
          horizontal: 'left',
          vertical:
            theme.shape.filter.height + parseInt(theme.spacing(2)) /* Filter height + 12px margin between Filter button and Menu seen in design */,
        }}
      >
        {searchable ? (
          <>
            <Box className={classes.searchFieldContainer}>
              <OutlinedInput
                className={classes.searchField}
                placeholder={t('filter.searchFieldPlaceholder')}
                size="medium"
                fullWidth
                value={searchQuery}
                onChange={e => {
                  setSearchQuery(e.target.value)
                }}
                onKeyDown={e => {
                  e.stopPropagation() // Prevents "shift + keyboard key" changing focus to menu items which is default MUI Menu behavior, this allow user to continue typing in search field
                }}
                inputProps={{
                  style: {
                    ...theme.typography.caption,
                  },
                }}
                endAdornment={
                  <InputAdornment position="end">
                    <IconButton onClick={clearSearchQuery} size="large" className={cx({ [classes.hidden]: searchQuery === '' })}>
                      <J2Multiply />
                    </IconButton>
                  </InputAdornment>
                }
                data-qa="filter-search-field"
                aria-label={`${label || 'filter'} options search`}
              />
            </Box>

            {filteredOptions.length === 0 ? (
              <Typography variant="body2" className={classes.noSearchResultsLabel}>
                {t('filter.noSearchResultsMessage')}
              </Typography>
            ) : null}
          </>
        ) : null}

        <Box className={classes.menuItems}>
          {filteredOptions.map(option => {
            const optionSelected = selectedValues.includes(option.value)

            return (
              <MenuItem
                key={`${option.label}-${option.value}`} // key generated as implemented in react-select when using generic type as value
                className={classes.menuItem}
                selected={optionSelected}
                dense
                onClick={() => {
                  handleFilterOptionClick(option)
                }}
                data-qa={`filter-option-${option.value}`}
              >
                <Checkbox size="small" checked={optionSelected} className={classes.checkbox} data-qa={`filter-option-${option.value}-checkbox`} />
                {option.optionIcon ? <Box className={classes.optionIcon}>{option.optionIcon}</Box> : null}
                {option.menuItemContent ?? <Typography variant="caption">{option.label}</Typography>}
              </MenuItem>
            )
          })}
        </Box>

        <Divider className={classes.divider} />

        <MenuItem dense onClick={handleClearAll} disabled={!hasSelection} className={classes.clearAllMenuItem} data-qa={'filter-clear-all'}>
          {t('filter.clearAllButtonText')}
        </MenuItem>
      </Menu>
    </>
  )
}
